/**
 * SM-2 Spaced Repetition Algorithm
 *
 * Simplified implementation of the SuperMemo 2 algorithm for scheduling
 * flashcard reviews. All functions are pure — no side effects.
 *
 * Rating scale:
 *   0 = Blackout  — "I had no idea"
 *   1 = Hard      — "I got it wrong but recognized the answer"
 *   2 = Good      — "I got it right with effort"
 *   3 = Easy      — "Instant recall"
 *
 * Algorithm:
 *   - Ratings 0-1: Reset repetitions to 0, interval to 1 day
 *   - Rating 2: Advance interval normally
 *   - Rating 3: Advance interval with bonus multiplier
 *   - Easiness factor adjusted each review, minimum 1.3
 *   - Initial intervals: 1 day, then 3 days, then previous * EF
 */

import { today, addDays } from '../../../shared/utils/dateUtils';

const MIN_EASINESS = 1.3;
const EASY_BONUS = 1.3; // Extra multiplier for "Easy" rating

/**
 * Calculate the next review state after a rating.
 *
 * @param {Object} srData - Current spaced repetition data
 * @param {number} srData.easiness_factor - Current EF (default 2.5)
 * @param {number} srData.interval_days - Current interval
 * @param {number} srData.repetitions - Consecutive correct reviews
 * @param {number} rating - User's self-assessment (0-3)
 * @returns {Object} Updated SR data with next_review_date
 */
export function calculateNext(srData, rating) {
  let { easiness_factor: ef, interval_days: interval, repetitions } = srData;

  // Adjust easiness factor based on rating
  // Modified SM-2 formula for 4-point scale (0-3 mapped to quality 0-5)
  const quality = rating * (5 / 3); // Map 0-3 to 0-5
  ef = ef + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
  ef = Math.max(MIN_EASINESS, ef);

  if (rating <= 1) {
    // Blackout or Hard: reset
    repetitions = 0;
    interval = 1;
  } else {
    // Good or Easy: advance
    repetitions += 1;

    if (repetitions === 1) {
      interval = 1;
    } else if (repetitions === 2) {
      interval = 3;
    } else {
      interval = Math.round(interval * ef);
    }

    // Easy bonus: extend interval further
    if (rating === 3) {
      interval = Math.round(interval * EASY_BONUS);
    }
  }

  // Ensure minimum interval of 1 day
  interval = Math.max(1, interval);

  const nextReviewDate = addDays(today(), interval);

  return {
    easiness_factor: Math.round(ef * 100) / 100, // Round to 2 decimal places
    interval_days: interval,
    repetitions,
    next_review_date: nextReviewDate,
    last_review_date: today(),
  };
}

/**
 * Check if a rating counts as "correct" for stats purposes.
 * Good (2) and Easy (3) are correct; Blackout (0) and Hard (1) are not.
 */
export function isCorrectRating(rating) {
  return rating >= 2;
}

/**
 * Create initial SR data for a new card.
 */
export function createInitialSRData() {
  return {
    easiness_factor: 2.5,
    interval_days: 0,
    repetitions: 0,
    next_review_date: today(),
    last_review_date: null,
  };
}
